import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';
import { Ingredient } from 'public-contracts';

export interface IngredientCreateEntry {
  name: string;
  description: string;
  imageId: string;
  recipeId?: string; // Optional reference to a Recipe if this ingredient is composed from other ingredients
}

export interface IngredientResponse {
  success: boolean;
  data: Ingredient;
  message?: string;
}

export interface IngredientListResponse {
  success: boolean;
  data: Ingredient[];
  total?: number;
}

class IngredientsService {
  private apiRoot = configs.apiRoot;

  async getIngredients(params?: {
    page?: number;
    perPage?: number;
    size?: number;
    filter?: any;
    sort?: any;
    q?: string;
  }): Promise<
    Ingredient[] & { __total?: number; __page?: number; __perPage?: number }
  > {
    const queryParams = new URLSearchParams();
    const page = params?.page ?? 1;
    // Support both size and perPage parameters, default to 20
    let size = 20;
    if (params?.size) {
      size = params.size;
    } else if (params?.perPage) {
      size = params.perPage;
    }

    // CRITICAL: API requires 'size' parameter, not 'perPage'
    queryParams.append('page', page.toString());
    queryParams.append('size', size.toString());
    if (params?.q) queryParams.append('q', params.q);
    if (params?.filter)
      queryParams.append('filter', JSON.stringify(params.filter));
    if (params?.sort) {
      // Backend expects sort as JSON array: ["field", "ASC"] or ["field", "DESC"]
      // If sort is an object like { name: 1 }, convert it to array format
      if (typeof params.sort === 'object' && !Array.isArray(params.sort)) {
        const sortEntries = Object.entries(params.sort);
        if (sortEntries.length > 0) {
          const [field, order] = sortEntries[0];
          const orderStr = order === 1 || order === 'ASC' || order === 'asc' ? 'ASC' : 'DESC';
          queryParams.append('sort', JSON.stringify([field, orderStr]));
        }
      } else {
        queryParams.append('sort', JSON.stringify(params.sort));
      }
    }

    const url = `${this.apiRoot}/ingredients${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;
    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to fetch ingredients: ${response.statusText}`
      );
    }

    const result = await response.json();
    let ingredients: Ingredient[] = [];

    // Handle both array and object with data property
    if (Array.isArray(result)) {
      ingredients = result;
    } else {
      ingredients = result.data || result || [];
    }

    // Check Content-Range header for total count
    const contentRange = response.headers.get('Content-Range');
    let total = ingredients.length;

    if (contentRange) {
      // Content-Range format: "items 0-19/100"
      const match = contentRange.match(/\/(\d+)$/);
      if (match) {
        total = parseInt(match[1], 10);
      }
    }

    // Attach metadata to the array
    const resultWithMeta = ingredients as Ingredient[] & {
      __total?: number;
      __page?: number;
      __perPage?: number;
    };
    resultWithMeta.__total = total;
    resultWithMeta.__page = page;
    resultWithMeta.__perPage = size;

    return resultWithMeta;
  }

  async getIngredient(id: string): Promise<Ingredient> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/ingredients/${id}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to fetch ingredient: ${response.statusText}`
      );
    }

    const result: IngredientResponse = await response.json();
    return result.data;
  }

  async createIngredient(data: IngredientCreateEntry): Promise<Ingredient> {
    const response = await authenticatedFetch(`${this.apiRoot}/ingredients`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to create ingredient: ${response.statusText}`
      );
    }

    const result: IngredientResponse = await response.json();
    return result.data;
  }

  async updateIngredient(
    id: string,
    data: Partial<IngredientCreateEntry>
  ): Promise<Ingredient> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/ingredients/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to update ingredient: ${response.statusText}`
      );
    }

    const result: IngredientResponse = await response.json();
    return result.data;
  }

  async deleteIngredient(id: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/ingredients/${id}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to delete ingredient: ${response.statusText}`
      );
    }
  }
}

export const ingredientsService = new IngredientsService();
